import { ApiService } from "@/services/api/ApiService";
import type { SetupAnalysisResult } from "@/types/agent/setup";
import type {
  ClubAssociationCreateApiResponse,
  ClubAssociationEditInput,
  ClubAssociationOnboardingInput,
  ClubAssociationOnboardingResult,
} from "@/types/customer-onboarding";

const DEFAULT_VOICE_ID = "hpp4J3VqNfWAUOO0d1Us";

export class ClubAssociationOnboardingService {
  static async createGenie(input: ClubAssociationOnboardingInput): Promise<ClubAssociationOnboardingResult> {
    const payload = buildClubAssociationAnalysis(input);

    const response = await ApiService.invoke<ClubAssociationCreateApiResponse | null>({
      resource: "genies",
      action: "create",
      data: payload,
    });

    const agent = response?.agent || response?.data || (response?.id ? (response as Agent) : null);

    return {
      agent,
      agentId: agent?.id || response?.id || null,
    };
  }

  static async updateDetails(input: ClubAssociationEditInput) {
    const onboardingDetails = buildOnboardingDetails(input);

    return ApiService.invoke({
      resource: "genies",
      action: "update",
      id: input.agent.id,
      data: {
        redirect_url: normalizeWebsite(input.website),
        description: buildDescription(input),
        settings: {
          support_team_emails: [input.email],
          conversation_config: {
            agent: {
              dynamic_variables: {
                dynamic_variable_placeholders: onboardingDetails,
              },
            },
          },
        },
        page: {
          info_links: {
            onboarding: onboardingDetails,
          },
        },
      },
    });
  }
}

function buildClubAssociationAnalysis(input: ClubAssociationOnboardingInput): SetupAnalysisResult {
  const website = normalizeWebsite(input.website);
  const onboardingDetails = buildOnboardingDetails({ ...input, website });

  return {
    setupConversationId: `club-association-${Date.now()}`,
    genieName: "Club Reception Genie",
    useCase: "Clubs and associations member support",
    language: "en",
    voicePreference: "Friendly, clear, warm, and helpful",
    voiceId: DEFAULT_VOICE_ID,
    conversationSettings: {
      maxDuration: 1800,
      turnTimeout: 7,
      silenceTimeout: 300,
      turnMode: "silence",
    },
    systemPrompt: [
      "You are a helpful front desk genie for a club or association.",
      "Answer common questions, collect useful contact details, and guide members or visitors to the right next step.",
      `Use this website as the primary source of public information: ${website}.`,
      `The club contact email is ${input.email}.`,
      `The club phone number is ${input.phone}.`,
      "When you are unsure, offer to take a message for the team.",
    ].join(" "),
    firstMessage: "Hi, thanks for getting in touch. How can I help today?",
    webWelcomeMessage: "Ask a question, leave a message, or get help from the club.",
    confidence: 0.82,
    customerEmail: input.email,
    brand: "Club or association",
    category: "clubs-associations",
    description: buildDescription(input),
    supportEmails: [input.email],
    sendConversationReports: true,
    attachPhoneNumber: false,
    redirectUrl: website,
    dynamicVariables: onboardingDetails,
    knowledgeBase: [
      {
        id: "club-website",
        name: "Club website",
        type: "url",
        content: website,
      },
    ],
    deferredDocumentActions: [
      {
        type: "website",
        data: {
          url: website,
          fileName: "Club website",
        },
      },
    ],
    isPublic: false,
    isMarketplaceGenie: false,
    isDemoGenie: false,
    agentType: "club_association",
  };
}

function buildDescription(input: ClubAssociationOnboardingInput): string {
  return `A simple front desk genie for a club or association. Website: ${normalizeWebsite(input.website)}. Phone: ${input.phone}. Email: ${input.email}.`;
}

function buildOnboardingDetails(input: ClubAssociationOnboardingInput): Record<string, string> {
  return {
    website: normalizeWebsite(input.website),
    phone: input.phone.trim(),
    email: input.email.trim(),
  };
}

function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
